import { MonetizationOnOutlined } from "@mui/icons-material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  Box,
  Button,
  Card,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from "@mui/material";
import { LoadingScreen } from "components/LoadingScreen";
import { useChurchStore } from "features/churches";
import moment from "moment";
import { parseAsString, useQueryState } from "nuqs";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useBalanceSheet, useExportBalanceSheet } from "../api";
import { EthiopianDateField } from "./EthiopianDateField";

export const BalanceSheet = () => {
  const [startDate, setStartDate] = useQueryState(
    "startDate",
    parseAsString.withDefault(moment().startOf("month").format("YYYY-MM-DD"))
  );
  const [endDate, setEndDate] = useQueryState(
    "endDate",
    parseAsString.withDefault(moment().format("YYYY-MM-DD"))
  );
  const [filterDates, setFilterDates] = useState({ startDate, endDate });

  const selectedChurch = useChurchStore(state => state.selectedChurch);

  const navigate = useNavigate();

  const { data: balanceSheet, isLoading } = useBalanceSheet({
    churchId: selectedChurch?.id,
    startDate: filterDates.startDate,
    endDate: filterDates.endDate
  });

  const { mutate: exportBalanceSheet, isLoading: isExporting } =
    useExportBalanceSheet();

  const handleFilter = () => {
    setFilterDates({ startDate, endDate });
  };

  const summaryData = {
    totalAssets:
      Number(balanceSheet?.totals?.total_assets).toLocaleString() || 0,
    totalLiabilities:
      Number(balanceSheet?.totals?.total_liabilities).toLocaleString() || 0,
    totalEquity:
      Number(balanceSheet?.totals?.total_equity).toLocaleString() || 0,
    totalLiabilitiesAndEquity:
      Number(
        balanceSheet?.totals?.total_liabilities_and_equity
      ).toLocaleString() || 0
  };

  const balanceSheetData = {
    assets: balanceSheet?.data?.assets || [],
    liabilities: balanceSheet?.data?.liabilities || [],
    equity: balanceSheet?.data?.equity || []
  };

  if (isLoading) return <LoadingScreen />;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h6" fontWeight={700}>
          Balance Sheet
        </Typography>
        <Box>
          <IconButton
            onClick={() =>
              exportBalanceSheet({
                churchId: selectedChurch?.id,
                queries: {
                  startDate: filterDates.startDate,
                  endDate: filterDates.endDate
                }
              })
            }
            disabled={isExporting}
          >
            <FileDownloadIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 3, width: "50%" }}>
        <EthiopianDateField
          size="small"
          label="Start date"
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
        />
        <EthiopianDateField
          size="small"
          label="End date"
          value={endDate}
          onChange={e => setEndDate(e.target.value)}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleFilter}
          sx={{ padding: "8px 32px", borderRadius: "12px" }}
        >
          Filter
        </Button>
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 4 }}>
        <Card elevation={0} sx={{ flex: 1, p: 2, backgroundColor: "#FAFAFA" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography color="textSecondary">Total Assets</Typography>
            <MonetizationOnOutlined color="action" sx={{ color: "#00A76F" }} />
          </Box>
          <Typography variant="h6" fontWeight={700}>
            {summaryData.totalAssets} ETB
          </Typography>
        </Card>
        <Card elevation={0} sx={{ flex: 1, p: 2, backgroundColor: "#FAFAFA" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography color="textSecondary">Total Liabilities</Typography>
            <MonetizationOnOutlined color="action" sx={{ color: "#FF5630" }} />
          </Box>
          <Typography variant="h6" fontWeight={700}>
            {summaryData.totalLiabilities} ETB
          </Typography>
        </Card>
        <Card elevation={0} sx={{ flex: 1, p: 2, backgroundColor: "#FAFAFA" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography color="textSecondary">Total Equity</Typography>
            <MonetizationOnOutlined color="action" sx={{ color: "#0C53B7" }} />
          </Box>
          <Typography variant="h6" fontWeight={700}>
            {summaryData.totalEquity} ETB
          </Typography>
        </Card>
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: "1px solid #E0E0E0",
          borderRadius: "12px",
          p: 3
        }}
      >
        <Table>
          <TableBody>
            <TableRow>
              <TableCell colSpan={2}>
                <Typography variant="h6" textAlign="center" fontWeight={700}>
                  Balance Sheet
                </Typography>
              </TableCell>
            </TableRow>

            {/* Assets Section */}
            <TableRow>
              <TableCell colSpan={2}>
                <Typography fontWeight="bold" sx={{ color: "text.secondary" }}>
                  ASSETS
                </Typography>
              </TableCell>
            </TableRow>
            {balanceSheetData.assets.map(item => (
              <TableRow key={item.id}>
                <TableCell
                  onClick={() => {
                    navigate(
                      `/finances/temp/chart-of-accounts/${
                        item.id
                      }?accountName=${item.account_name}&accountNumber=${
                        item.account_number
                      }&accountType=${item.account_type || ""}`
                    );
                  }}
                  sx={{
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" }
                  }}
                >
                  {item.account_name}
                </TableCell>
                <TableCell align="right">
                  {Number(item.total).toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>TOTAL ASSETS</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>
                {summaryData.totalAssets} ETB
              </TableCell>
            </TableRow>

            {/* Liabilities Section */}
            <TableRow>
              <TableCell colSpan={2}>
                <Typography fontWeight="bold" sx={{ color: "text.secondary" }}>
                  LIABILITIES
                </Typography>
              </TableCell>
            </TableRow>
            {balanceSheetData.liabilities.map(item => (
              <TableRow key={item.id}>
                <TableCell
                  onClick={() => {
                    navigate(
                      `/finances/temp/chart-of-accounts/${
                        item.id
                      }?accountName=${item.account_name}&accountNumber=${
                        item.account_number
                      }&accountType=${item.account_type || ""}`
                    );
                  }}
                  sx={{
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" }
                  }}
                >
                  {item.account_name}
                </TableCell>
                <TableCell align="right">
                  {Number(item.total).toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>
                TOTAL LIABILITIES
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>
                {summaryData.totalLiabilities} ETB
              </TableCell>
            </TableRow>

            {/* Equity Section */}
            <TableRow>
              <TableCell colSpan={2}>
                <Typography fontWeight="bold" sx={{ color: "text.secondary" }}>
                  EQUITY
                </Typography>
              </TableCell>
            </TableRow>
            {balanceSheetData.equity.map(item => (
              <TableRow key={item.id}>
                <TableCell
                  onClick={() => {
                    navigate(
                      `/finances/temp/chart-of-accounts/${
                        item.id
                      }?accountName=${item.account_name}&accountNumber=${
                        item.account_number
                      }&accountType=${item.account_type || ""}`
                    );
                  }}
                  sx={{
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" }
                  }}
                >
                  {item.account_name}
                </TableCell>
                <TableCell align="right">
                  {Number(item.total).toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>TOTAL EQUITY</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>
                {summaryData.totalEquity} ETB
              </TableCell>
            </TableRow>

            {/* Total Liabilities and Equity */}
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>
                TOTAL LIABILITIES AND EQUITY
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>
                {summaryData.totalLiabilitiesAndEquity} ETB
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
